"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { ProviderServiceHint } from "@/components/provider-service-hint";
import { Button, Checkbox, Input, Label, Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { formatStaffName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { isProviderVisibleAtLocations } from "./provider-location-visibility";
export type ProviderSelectable = {
  id: string;
  firstName: string;
  lastName?: string;
  isArchived?: boolean;
  visibleLocationIds?: string[];
};
type ProviderGrouping = {
  inSelectedLocations: ProviderSelectable[];
  inOtherLocations: ProviderSelectable[];
  deletedProviders: ProviderSelectable[];
};
function sortProviders(providers: ProviderSelectable[]) {
  return [...providers].sort((a, b) => {
    const nameA = formatStaffName(a).toLowerCase();
    const nameB = formatStaffName(b).toLowerCase();
    return nameA.localeCompare(nameB);
  });
}
function groupProviders(opts: {
  providers: ProviderSelectable[];
  selectedLocationIds: string[];
  searchTerm: string;
}): {
  filteredProviders: ProviderSelectable[];
  groupings: ProviderGrouping;
} {
  const {
    providers,
    selectedLocationIds,
    searchTerm
  } = opts;
  const filteredProviders = providers.filter(provider => formatStaffName(provider).toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredIdSet = new Set(filteredProviders.map(p => p.id));
  const selectedLocationSet = new Set(selectedLocationIds);
  const inSelectedLocations: ProviderSelectable[] = [];
  const inOtherLocations: ProviderSelectable[] = [];
  const deletedProviders: ProviderSelectable[] = [];
  for (const provider of providers) {
    if (!filteredIdSet.has(provider.id)) continue;
    if (provider.isArchived === true) {
      deletedProviders.push(provider);
      continue;
    }
    if (isProviderVisibleAtLocations(provider.visibleLocationIds, selectedLocationSet)) {
      inSelectedLocations.push(provider);
    } else {
      inOtherLocations.push(provider);
    }
  }
  return {
    filteredProviders,
    groupings: {
      inSelectedLocations: sortProviders(inSelectedLocations),
      inOtherLocations: sortProviders(inOtherLocations),
      deletedProviders: sortProviders(deletedProviders)
    }
  };
}
export type ProviderSelectProps = {
  align?: "start" | "end";
  className?: string;
  title?: string;
  searchPlaceholder?: string;
  showServiceHint?: boolean;
  providers: ProviderSelectable[];
  selectedProviderIds: string[];
  onChangeSelectedProviderIds: (next: string[]) => void;
  selectedLocationIds: string[];
};
export const ProviderSelect: React.FC<ProviderSelectProps> = t0 => {
  const $ = _c(109);
  const {
    className,
    align: t1,
    title: t2,
    searchPlaceholder: t3,
    showServiceHint: t4,
    providers,
    selectedProviderIds,
    onChangeSelectedProviderIds,
    selectedLocationIds
  } = t0;
  const align = t1 === undefined ? "start" : t1;
  const title = t2 === undefined ? "Selected Providers" : t2;
  const searchPlaceholder = t3 === undefined ? "Search for providers" : t3;
  const showServiceHint = t4 === undefined ? false : t4;
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const [tempProviderIds, setTempProviderIds] = useState(t5);
  const [isDeletedExpanded, setIsDeletedExpanded] = useState(false);
  let t6;
  let t7;
  if ($[1] !== isOpen || $[2] !== selectedProviderIds) {
    t6 = () => {
      if (!isOpen) {
        return;
      }
      setTempProviderIds(selectedProviderIds);
    };
    t7 = [isOpen, selectedProviderIds];
    $[1] = isOpen;
    $[2] = selectedProviderIds;
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[5] !== isOpen || $[6] !== providers) {
    t8 = () => {
      if (!isOpen) {
        return;
      }
      const valid = new Set(providers.map(_temp));
      setTempProviderIds(prev => prev.filter(id => valid.has(id)));
    };
    t9 = [isOpen, providers];
    $[5] = isOpen;
    $[6] = providers;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  useEffect(t8, t9);
  let t10;
  if ($[9] !== providers || $[10] !== searchTerm || $[11] !== selectedLocationIds) {
    t10 = groupProviders({
      providers,
      selectedLocationIds,
      searchTerm
    });
    $[9] = providers;
    $[10] = searchTerm;
    $[11] = selectedLocationIds;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const {
    filteredProviders,
    groupings
  } = t10;
  let t11;
  if ($[13] !== groupings.deletedProviders || $[14] !== tempProviderIds) {
    let t12;
    if ($[16] !== tempProviderIds) {
      t12 = p_0 => tempProviderIds.includes(p_0.id);
      $[16] = tempProviderIds;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    t11 = groupings.deletedProviders.filter(t12);
    $[13] = groupings.deletedProviders;
    $[14] = tempProviderIds;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const selectedDeletedCount = t11.length;
  const isDeletedOpen = isDeletedExpanded || searchTerm.length > 0;
  let t12;
  if ($[18] !== providers) {
    t12 = new Set(providers.filter(_temp2).map(_temp3));
    $[18] = providers;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const deletedIds = t12;
  let t13;
  bb0: {
    if (selectedProviderIds.length === 0 || providers.length === 0) {
      t13 = "0";
      break bb0;
    }
    if (selectedProviderIds.length === providers.length) {
      t13 = "All";
      break bb0;
    }
    let t14;
    if ($[20] !== selectedProviderIds.length) {
      t14 = selectedProviderIds.length.toString();
      $[20] = selectedProviderIds.length;
      $[21] = t14;
    } else {
      t14 = $[21];
    }
    t13 = t14;
  }
  const displayText = t13;
  let t14;
  if ($[22] !== tempProviderIds) {
    t14 = providerId => tempProviderIds.includes(providerId);
    $[22] = tempProviderIds;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const isTempSelected = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = (providerId_0, checked) => {
      if (checked) {
        setTempProviderIds(prev_0 => prev_0.includes(providerId_0) ? prev_0 : [...prev_0, providerId_0]);
      } else {
        setTempProviderIds(prev_1 => prev_1.filter(id_0 => id_0 !== providerId_0));
      }
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const toggleProvider = t15;
  let t16;
  if ($[25] !== deletedIds || $[26] !== groupings.deletedProviders || $[27] !== groupings.inOtherLocations || $[28] !== groupings.inSelectedLocations || $[29] !== isDeletedOpen) {
    t16 = () => {
      const nonDeleted = [...groupings.inSelectedLocations, ...groupings.inOtherLocations].map(_temp4);
      if (!isDeletedOpen) {
        setTempProviderIds(prev_2 => [...nonDeleted, ...prev_2.filter(id_1 => deletedIds.has(id_1))]);
        return;
      }
      setTempProviderIds([...nonDeleted, ...groupings.deletedProviders.map(_temp5)]);
    };
    $[25] = deletedIds;
    $[26] = groupings.deletedProviders;
    $[27] = groupings.inOtherLocations;
    $[28] = groupings.inSelectedLocations;
    $[29] = isDeletedOpen;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const handleSelectAll = t16;
  let t17;
  if ($[31] !== deletedIds || $[32] !== isDeletedOpen) {
    t17 = () => {
      if (!isDeletedOpen) {
        setTempProviderIds(prev_3 => prev_3.filter(id_2 => deletedIds.has(id_2)));
        return;
      }
      setTempProviderIds([]);
    };
    $[31] = deletedIds;
    $[32] = isDeletedOpen;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const handleSelectNone = t17;
  let t18;
  if ($[34] !== onChangeSelectedProviderIds || $[35] !== tempProviderIds) {
    t18 = () => {
      if (tempProviderIds.length === 0) {
        return;
      }
      onChangeSelectedProviderIds(tempProviderIds);
      setIsOpen(false);
    };
    $[34] = onChangeSelectedProviderIds;
    $[35] = tempProviderIds;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const handleApply = t18;
  let t19;
  if ($[37] !== selectedProviderIds) {
    t19 = () => {
      setTempProviderIds(selectedProviderIds);
      setIsOpen(false);
    };
    $[37] = selectedProviderIds;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const handleCancel = t19;
  const canApply = tempProviderIds.length > 0;
  let t20;
  if ($[39] !== selectedProviderIds) {
    t20 = open => {
      setIsOpen(open);
      if (open) {
        setTempProviderIds(selectedProviderIds);
      }
    };
    $[39] = selectedProviderIds;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const t21 = (selectedProviderIds.length === 0 || providers.length > 0 && selectedProviderIds.length === providers.length) && "text-caption";
  let t22;
  if ($[41] !== t21) {
    t22 = cx("flex shrink-0 items-center justify-center rounded bg-muted px-1 tabular-nums text-foreground", t21);
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== displayText || $[44] !== t22) {
    t23 = <span className={t22}>{displayText}</span>;
    $[43] = displayText;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== t23 || $[47] !== title) {
    t24 = <PopoverTrigger asChild={true} className="w-full"><Button variant="secondary" className="flex w-full justify-start gap-1.5 font-normal">{title}{t23}</Button></PopoverTrigger>;
    $[46] = t23;
    $[47] = title;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== filteredProviders || $[50] !== searchTerm || $[51] !== tempProviderIds.length) {
    t25 = tempProviderIds.length === 0 ? <div className="py-0.5 text-caption text-status-error-text">You must select at least 1 provider</div> : filteredProviders.length === 0 ? <div className="py-0.5 text-caption text-muted-foreground">{searchTerm ? "No providers found" : "No providers available"}</div> : <div className="py-0.5 text-caption text-muted-foreground">Select providers from the list below.</div>;
    $[49] = filteredProviders;
    $[50] = searchTerm;
    $[51] = tempProviderIds.length;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = e => setSearchTerm(e.target.value);
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== searchPlaceholder || $[55] !== searchTerm) {
    t27 = <Input placeholder={searchPlaceholder} value={searchTerm} onChange={t26} className="w-full rounded-xl bg-background/90 !text-caption shadow-md backdrop-blur-3xl" />;
    $[54] = searchPlaceholder;
    $[55] = searchTerm;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== handleSelectAll) {
    t28 = <Button variant="secondary" size="sm" className="w-12 rounded-lg bg-background/80 backdrop-blur-sm" onClick={handleSelectAll}>All</Button>;
    $[57] = handleSelectAll;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== handleSelectNone) {
    t29 = <Button variant="secondary" size="sm" className="w-12 rounded-lg bg-background/80 backdrop-blur-sm" onClick={handleSelectNone}>None</Button>;
    $[59] = handleSelectNone;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t28 || $[62] !== t29) {
    t30 = <div className="mt-1 flex w-full flex-row justify-end gap-1 *:shadow-md">{t28}{t29}</div>;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== t25 || $[65] !== t27 || $[66] !== t30) {
    t31 = <div className="sticky top-0 z-10 bg-gradient-to-b from-background via-background/85 to-transparent px-1 py-2 text-center">{t25}{t27}{t30}</div>;
    $[64] = t25;
    $[65] = t27;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== groupings.inOtherLocations.length || $[69] !== groupings.inSelectedLocations || $[70] !== isTempSelected) {
    t32 = groupings.inSelectedLocations.length > 0 && <div>{groupings.inOtherLocations.length > 0 && <div className="mb-1 text-left text-caption-strong text-muted-foreground">Provider{groupings.inSelectedLocations.length > 1 && <span>{"s"}</span>}<span> at selected location</span>{groupings.inSelectedLocations.length > 1 && <span>{"s"}</span>}</div>}{groupings.inSelectedLocations.map(provider => {
        const isChecked = isTempSelected(provider.id);
        return <div key={provider.id} className="mb-2 flex items-center gap-1.5"><Checkbox id={`provider-selected-${provider.id}`} checked={isChecked} onCheckedChange={checked_0 => toggleProvider(provider.id, checked_0 === true)} /><Label htmlFor={`provider-selected-${provider.id}`} className="flex w-full flex-row items-center gap-1 text-body"><span>{provider.firstName} {provider.lastName}</span></Label></div>;
      })}</div>;
    $[68] = groupings.inOtherLocations.length;
    $[69] = groupings.inSelectedLocations;
    $[70] = isTempSelected;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== groupings.inOtherLocations || $[73] !== groupings.inSelectedLocations.length || $[74] !== isTempSelected) {
    t33 = groupings.inOtherLocations.length > 0 && <div>{groupings.inSelectedLocations.length > 0 && <div className="mb-1 text-left text-caption-strong text-muted-foreground">Provider{groupings.inOtherLocations.length > 1 && <span>{"s"}</span>}<span> at other location</span>{groupings.inOtherLocations.length > 1 && <span>{"s"}</span>}</div>}{groupings.inOtherLocations.map(provider_0 => {
        const isChecked_0 = isTempSelected(provider_0.id);
        return <div key={provider_0.id} className="mb-2 flex items-center gap-1.5"><Checkbox id={`provider-other-${provider_0.id}`} checked={isChecked_0} onCheckedChange={checked_1 => toggleProvider(provider_0.id, checked_1 === true)} /><Label htmlFor={`provider-other-${provider_0.id}`} className="flex w-full flex-row items-center gap-1 text-body"><span>{formatStaffName(provider_0)}</span></Label></div>;
      })}</div>;
    $[72] = groupings.inOtherLocations;
    $[73] = groupings.inSelectedLocations.length;
    $[74] = isTempSelected;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== groupings.deletedProviders || $[77] !== isDeletedOpen || $[78] !== isTempSelected || $[79] !== selectedDeletedCount) {
    t34 = groupings.deletedProviders.length > 0 && <Collapsible open={isDeletedOpen} onOpenChange={setIsDeletedExpanded}><CollapsibleTrigger asChild={true}><button type="button" aria-expanded={isDeletedOpen} className="mb-1 flex w-full items-center gap-1 text-left text-body-strong text-foreground-secondary"><FiChevronDown className={cx("size-3 shrink-0 transition-transform", !isDeletedOpen && "-rotate-90")} aria-hidden="true" /><span>Deleted provider{groupings.deletedProviders.length > 1 && <span>{"s"}</span>}</span><span className="shrink-0 rounded bg-muted px-1 tabular-nums text-foreground">{selectedDeletedCount}/{groupings.deletedProviders.length}</span></button></CollapsibleTrigger><CollapsibleContent>{groupings.deletedProviders.map(provider_1 => {
          const isChecked_1 = isTempSelected(provider_1.id);
          return <div key={provider_1.id} className="mb-2 flex items-center gap-1.5"><Checkbox id={`provider-deleted-${provider_1.id}`} checked={isChecked_1} onCheckedChange={checked_2 => toggleProvider(provider_1.id, checked_2 === true)} /><Label htmlFor={`provider-deleted-${provider_1.id}`} className="flex w-full flex-row items-center gap-1 text-body"><span>{formatStaffName(provider_1)}</span></Label></div>;
        })}</CollapsibleContent></Collapsible>;
    $[76] = groupings.deletedProviders;
    $[77] = isDeletedOpen;
    $[78] = isTempSelected;
    $[79] = selectedDeletedCount;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== t32 || $[82] !== t33 || $[83] !== t34) {
    t35 = <div className="min-h-24">{t32}{t33}{t34}</div>;
    $[81] = t32;
    $[82] = t33;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== showServiceHint) {
    t36 = showServiceHint && <ProviderServiceHint className="max-w-[70vw] px-1 pb-1 md:max-w-72" />;
    $[85] = showServiceHint;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== handleCancel) {
    t37 = <Button variant="secondary" className="h-8" type="button" onClick={handleCancel}>Cancel</Button>;
    $[87] = handleCancel;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  const t38 = !canApply;
  let t39;
  if ($[89] !== handleApply || $[90] !== t38) {
    t39 = <Button variant="primary" className="h-8" type="button" onClick={handleApply} disabled={t38}>Apply</Button>;
    $[89] = handleApply;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t37 || $[93] !== t39) {
    t40 = <div className="sticky bottom-0 right-0 z-10 flex items-end justify-end space-x-2 bg-gradient-to-t from-background via-background/85 to-transparent pb-4 pr-2 *:!shadow-xl">{t37}{t39}</div>;
    $[92] = t37;
    $[93] = t39;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== align || $[96] !== t31 || $[97] !== t35 || $[98] !== t36 || $[99] !== t40) {
    t41 = <PopoverContent align={align} className="h-full max-h-96 min-h-24 overflow-y-auto p-0 pl-4 pr-2">{t31}{t35}{t36}{t40}</PopoverContent>;
    $[95] = align;
    $[96] = t31;
    $[97] = t35;
    $[98] = t36;
    $[99] = t40;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== isOpen || $[102] !== t20 || $[103] !== t24 || $[104] !== t41) {
    t42 = <Popover modal={true} open={isOpen} onOpenChange={t20}>{t24}{t41}</Popover>;
    $[101] = isOpen;
    $[102] = t20;
    $[103] = t24;
    $[104] = t41;
    $[105] = t42;
  } else {
    t42 = $[105];
  }
  let t43;
  if ($[106] !== className || $[107] !== t42) {
    t43 = <div className={className}>{t42}</div>;
    $[106] = className;
    $[107] = t42;
    $[108] = t43;
  } else {
    t43 = $[108];
  }
  return t43;
};
function _temp(p) {
  return p.id;
}
function _temp2(p_1) {
  return p_1.isArchived === true;
}
function _temp3(p_2) {
  return p_2.id;
}
function _temp4(p_3) {
  return p_3.id;
}
function _temp5(p_4) {
  return p_4.id;
}